/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useFeatureCategoryData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching Feature Category data and available feature codes.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import {
  fetchFeatureCategoryById,
  fetchAvailableFeatureCodes,
} from '../api/featureCategoriesApi';
import { FormState, FeatureCode } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';

const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i; // Accepts numeric and Sqid-encoded IDs


/**
 * Hook for fetching Feature Category data
 */
export function useFeatureCategoryData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState | null>(null);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<number>>(new Set());
  const [availableFeatures, setAvailableFeatures] = useState<FeatureCode[]>([]);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const featuresLoadedRef = useRef(false);
  const tRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
  }, [showError, t]);

  const loadFeatureCategory = useCallback(
    async (loadId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const payload = await fetchFeatureCategoryById(loadId, tRef.current);

        const members = Array.isArray(payload.members)
          ? payload.members.map((member) => member.featureCodeId)
          : [];

        const copySuffix = tRef.current('common.copySuffix', 'Copy');
        const resolvedName = duplicate
          ? `${payload.name ?? ''} ${copySuffix}`.trim()
          : (payload.name ?? '');

        const normalised: FormState = {
          name: resolvedName.trim(),
          description: (payload.description ?? '').trim(),
          enabled: payload.enabled ?? true,
        };

        const memberSet = new Set<number>(members);

        setFormData(normalised);
        setSelectedFeatureIds(memberSet);
      } catch (error) {
        showErrorRef.current(tRef.current('featureCategories.messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'featureCategories:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLoadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && loadId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;

        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        if (!duplicateId) {
          // Reset form to default values
          setFormData(getDefaultFormState());
          setSelectedFeatureIds(new Set());
        }
      }
      return;
    }

    if (!ID_PATTERN.test(loadId)) {
      showErrorRef.current(tRef.current('featureCategories.messages.loadError'));
      lastLoadedIdRef.current = null;
      return;
    }

    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    loadFeatureCategory(loadId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadFeatureCategory, location.key, location.pathname, location.search]);

  useEffect(() => {
    if (featuresLoadedRef.current) {
      return;
    }
    featuresLoadedRef.current = true;

    const loadFeatures = async () => {
      try {
        const features = await fetchAvailableFeatureCodes(tRef.current);
        setAvailableFeatures(features);
      } catch (error) {
        featuresLoadedRef.current = false;
        const message =
          error instanceof Error
            ? error.message
            : tRef.current('featureCategories.messages.loadError');
        showErrorRef.current(message);
      }
    };

    loadFeatures();
  }, []);

  return {
    isLoading,
    formData,
    setFormData,
    selectedFeatureIds,
    setSelectedFeatureIds,
    availableFeatures,
  };
}

